import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { getAnalyticsPage, isAnalyticsEnabled, trackEvent } from '../utils/analytics';

/**
 * ユーザー行動の詳細トラッキングフック
 *
 * 計測対象:
 * - ブラウザバック / ブラウザフォワード
 * - ページリロード
 * - スワイプ（モバイル）
 * - タブ切り替え・最小化
 * - ページごとの滞在時間
 */
export function useUserBehaviorTracking() {
  const location = useLocation();
  const navigationType = useNavigationType(); // 'POP' = ブラウザバック/フォワード
  const pageEnteredAtRef = useRef(Date.now());

  // ブラウザバック / ブラウザフォワード検知
  // useNavigationType() が 'POP' になるのはブラウザの戻る/進むボタン操作時
  useEffect(() => {
    if (!isAnalyticsEnabled()) return;
    if (navigationType === 'POP') {
      trackEvent('browser_back', {
        page: getAnalyticsPage(location.pathname),
      });
    }
  }, [navigationType, location.pathname]);

  // ページリロード検知（Performance Navigation API）
  useEffect(() => {
    if (!isAnalyticsEnabled()) return;
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      if (nav?.type === 'reload') {
        trackEvent('page_reload', {
          page: getAnalyticsPage(location.pathname),
        });
      }
    } catch (_) {
      // Performance API 非対応ブラウザは無視
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // マウント時（= ページロード時）のみ実行

  // ページ滞在時間（ページ離脱時に送信）
  useEffect(() => {
    pageEnteredAtRef.current = Date.now();

    return () => {
      const durationSec = Math.round((Date.now() - pageEnteredAtRef.current) / 1000);
      // 2秒未満の瞬間遷移はノイズになるので除外
      if (durationSec >= 2 && isAnalyticsEnabled()) {
        trackEvent('page_leave', {
          page: getAnalyticsPage(location.pathname),
          duration_sec: durationSec,
        });
      }
    };
  }, [location.pathname]);

  // タブ切り替え・最小化検知（Page Visibility API）
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!isAnalyticsEnabled()) return;

      if (document.hidden) {
        const durationSec = Math.round((Date.now() - pageEnteredAtRef.current) / 1000);
        trackEvent('tab_hidden', {
          page: getAnalyticsPage(location.pathname),
          duration_sec: durationSec,
        });
      } else {
        pageEnteredAtRef.current = Date.now();
        trackEvent('tab_visible', {
          page: getAnalyticsPage(location.pathname),
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [location.pathname]);

  // スワイプ検知（モバイル）
  // 縦スクロールと区別するため、横方向が縦の1.5倍以上かつ60px以上の場合のみスワイプとみなす
  useEffect(() => {
    let startX = null;
    let startY = null;

    const onTouchStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };

    const onTouchEnd = (e) => {
      if (startX === null) return;
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;

      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        if (isAnalyticsEnabled()) {
          trackEvent('swipe', {
            page: getAnalyticsPage(location.pathname),
            direction: dx > 0 ? 'right' : 'left',
          });
        }
      }

      startX = null;
      startY = null;
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [location.pathname]);
}
